import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { type TTicketBySpace } from '../types/ticket.type';

export type TGetListTicketBySpaceResponse = ApiResponse<TTicketBySpace[]>;
export type TGetListTicketBySpaceParams = {
  spaceId: string;
};

export const getListTicketBySpace = async (
  params: TGetListTicketBySpaceParams
): Promise<TGetListTicketBySpaceResponse> => {
  const url = `/ticket/by-space/${params.spaceId}`;

  return axiosInstance.get(url, { params });
};

export const GET_LIST_TICKET_BY_SPACE_QUERY_KEY = 'get-list-ticketing-ticket-by-space-id';

export const getListTicketBySpaceQueryOptions = <TData = TGetListTicketBySpaceResponse>(
  params: TGetListTicketBySpaceParams,
  select?: (data: TGetListTicketBySpaceResponse) => TData
) => {
  return queryOptions({
    queryKey: [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId: params?.spaceId }],
    queryFn: () => getListTicketBySpace(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useListTicketBySpaceQuery<TData>(options: {
  params: TGetListTicketBySpaceParams;
  queryConfig?: QueryConfig<typeof getListTicketBySpaceQueryOptions<TData>>;
  select: (data: TGetListTicketBySpaceResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useListTicketBySpaceQuery(options: {
  params: TGetListTicketBySpaceParams;
  queryConfig?: QueryConfig<typeof getListTicketBySpaceQueryOptions<TGetListTicketBySpaceResponse>>;
}): ReturnType<typeof useQuery<TGetListTicketBySpaceResponse>>;

// Implementation
export function useListTicketBySpaceQuery<TData = TGetListTicketBySpaceResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListTicketBySpaceParams;
  queryConfig?: QueryConfig<typeof getListTicketBySpaceQueryOptions<TData | TGetListTicketBySpaceResponse>>;
  select?: (data: TGetListTicketBySpaceResponse) => TData;
}) {
  return useQuery({
    ...getListTicketBySpaceQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListTicketBySpaceResponse ? TGetListTicketBySpaceResponse : TData>
  >;
}
